import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  wishagentStatus, wishagentSubscribe, wishagentProfile, wishagentSaveProfile,
  wishagentSaveResume, wishagentSavePrefs, wishagentRun, wishagentApplications,
  wishagentApprove, wishagentDismiss,
} from "@/lib/api";
import { completePurchase } from "@/lib/razorpay";
import {
  Sparkles, Loader2, CheckCircle2, XCircle, ExternalLink, Send, Lock,
  FileText, Settings2, Play, ShieldCheck, Clock, Zap,
} from "lucide-react";

/* WishAgent — the agent that applies to jobs on the student's behalf.
   Copilot by default: proposals wait for a tap. Autopilot submits within
   the student's mandate. */

const STATUS_META = {
  proposed: { cls: "bg-amber-100 text-amber-700", label: "Awaiting your OK" },
  submitted: { cls: "bg-emerald-100 text-emerald-700", label: "Submitted" },
  prepared: { cls: "bg-blue-100 text-blue-700", label: "Prepared — 1 tap left" },
  needs_input: { cls: "bg-orange-100 text-orange-700", label: "Needs your input" },
  failed: { cls: "bg-red-100 text-red-600", label: "Failed" },
  dismissed: { cls: "bg-stone-100 text-stone-500", label: "Skipped" },
};

function Inp({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500">{label}</span>
      <input {...props} className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
    </label>
  );
}

export default function WishAgentPage() {
  // Admin-gated feature: direct URL hits bounce to the dashboard until enabled.
  const { user } = useAuth();
  if (user && !user.features?.wishagent) return <Navigate to="/app" replace />;
  return <WishAgentInner />;
}

function WishAgentInner() {
  const [st, setSt] = useState(null);
  const [apps, setApps] = useState(null);
  const [tab, setTab] = useState("agent"); // agent | profile
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [p, setP] = useState({});
  const [prefs, setPrefs] = useState({ roles: [], locations: [], blocklist: [], autonomy: "copilot" });
  const [rolesText, setRolesText] = useState("");
  const [locText, setLocText] = useState("");
  const [blockText, setBlockText] = useState("");

  const load = () => {
    wishagentStatus().then(({ data }) => {
      setSt(data);
      setPrefs(data.prefs);
      setRolesText((data.prefs.roles || []).join(", "));
      setLocText((data.prefs.locations || []).join(", "));
      setBlockText((data.prefs.blocklist || []).join(", "));
    }).catch(() => {});
    wishagentApplications().then(({ data }) => setApps(data)).catch(() => setApps([]));
    wishagentProfile().then(({ data }) => setP(data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setBusy("sub");
    try {
      const { data } = await wishagentSubscribe();
      await completePurchase(data); // Razorpay when live, instant in mock mode
      setMsg("WishAgent is active — set up your application profile and hit Run."); load();
    }
    catch (e) { setMsg(e?.message || e?.response?.data?.detail || "Payment failed."); }
    finally { setBusy(null); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy("profile"); setMsg(null);
    try {
      await wishagentSaveProfile({
        full_name: p.full_name || "", email: p.email || "", phone: p.phone || "",
        city: p.city || undefined, linkedin_url: p.linkedin_url || undefined,
        github_url: p.github_url || undefined, portfolio_url: p.portfolio_url || undefined,
        degree: p.degree || undefined, college: p.college || undefined,
        grad_year: p.grad_year ? Number(p.grad_year) : undefined,
        cgpa: p.cgpa ? Number(p.cgpa) : undefined,
        work_authorization: p.work_authorization || undefined,
        notice_period: p.notice_period || undefined,
        expected_ctc: p.expected_ctc || undefined, about: p.about || undefined,
      });
      setMsg("Profile saved — the agent fills every form from this."); load();
    } catch (e2) { setMsg(e2?.response?.data?.detail || "Could not save."); }
    finally { setBusy(null); }
  };

  const uploadResume = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy("resume");
      try {
        await wishagentSaveResume({ filename: file.name, mime: file.type || "application/pdf", b64: String(reader.result).split(",")[1] });
        setMsg("Résumé attached."); load();
      } catch (e2) { setMsg(e2?.response?.data?.detail || "Upload failed."); }
      finally { setBusy(null); }
    };
    reader.readAsDataURL(file);
  };

  const savePrefs = async () => {
    setBusy("prefs");
    try {
      await wishagentSavePrefs({
        roles: rolesText.split(",").map((x) => x.trim()).filter(Boolean),
        locations: locText.split(",").map((x) => x.trim()).filter(Boolean),
        blocklist: blockText.split(",").map((x) => x.trim()).filter(Boolean),
        autonomy: prefs.autonomy,
      });
      setMsg("Mandate saved."); load();
    } catch (_) {}
    finally { setBusy(null); }
  };

  const run = async () => {
    setBusy("run"); setMsg(null);
    try {
      const { data } = await wishagentRun();
      setMsg(`Agent found ${data.proposed} new application${data.proposed === 1 ? "" : "s"} matching your mandate.`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Run failed."); }
    finally { setBusy(null); }
  };

  const act = async (fn, id) => {
    setBusy(id);
    try { await fn(); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  if (!st) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading WishAgent…</div>;

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-8">
        <span className="overline">WishAgent · Pro</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">Your agent applies. <span className="italic text-stone-500">You interview.</span></h1>
        <p className="mt-3 text-stone-600 max-w-2xl">WishAgent watches the fresher job market daily, matches openings to your mandate, writes your application note, and applies on company career sites — with your approval, or fully automatic.</p>
      </div>

      {msg && <p className="mb-6 text-sm rounded-xl bg-amber-50 border border-amber-200 text-stone-700 px-4 py-2.5" data-testid="wa-msg">{msg}</p>}

      {!st.active ? (
        <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 relative overflow-hidden wsh-grain" data-testid="wa-paywall">
          <div className="absolute inset-0 opacity-45 pointer-events-none" style={{ background: "radial-gradient(420px 260px at 90% 0%, rgba(217,119,6,0.45), transparent 60%)" }} />
          <div className="relative">
            <span className="inline-flex p-2.5 rounded-xl bg-amber-300 text-stone-900"><Lock size={18} /></span>
            <h2 className="font-display text-3xl mt-3">Stop applying. Start interviewing.</h2>
            <p className="mt-2 text-stone-300 max-w-lg">Freshers send 100+ applications to land one offer. WishAgent does the sending — every day, matched to your target role, with a personalized note on each one.</p>
            <div className="mt-5 rounded-2xl bg-white/10 border border-white/15 p-5 max-w-md">
              <p className="font-display text-2xl">WishAgent Pro — ₹{st.price_inr}/month</p>
              <ul className="mt-2 space-y-1.5 text-sm text-stone-200">
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Up to {st.monthly_cap} agent applications every month</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Personalized note written for every application</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Approval mode or full autopilot — you're always in control</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Live activity feed with proof of every application</li>
              </ul>
              <button onClick={subscribe} disabled={busy === "sub"} data-testid="wa-subscribe"
                className="mt-4 w-full px-5 py-3 rounded-full bg-amber-300 text-stone-900 font-semibold hover:bg-amber-200 disabled:opacity-60">
                {busy === "sub" ? <Loader2 size={16} className="animate-spin inline" /> : `Activate for ₹${st.price_inr}`}
              </button>
              <p className="mt-2 text-[11px] text-stone-400 text-center">Test mode — no real charge yet. Never uses your LinkedIn/Naukri password.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Status strip */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold"><Zap size={12} /> Active · {st.used_this_month}/{st.monthly_cap} used this month</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${st.profile_complete && st.has_resume ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
              <FileText size={12} /> {st.profile_complete ? (st.has_resume ? "Profile ready" : "Résumé PDF missing") : "Profile incomplete"}
            </span>
            {st.dry_run && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-200 text-stone-600 text-xs font-semibold"><ShieldCheck size={12} /> Safe test mode — nothing is really sent</span>}
            <div className="ml-auto flex gap-2">
              <button onClick={() => setTab(tab === "profile" ? "agent" : "profile")} className="btn-ghost text-sm"><Settings2 size={15} /> {tab === "profile" ? "Back to agent" : "Profile & mandate"}</button>
              <button onClick={run} disabled={busy === "run"} data-testid="wa-run" className="btn-primary text-sm disabled:opacity-60">
                {busy === "run" ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run now
              </button>
            </div>
          </div>

          {tab === "profile" ? (
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              <form onSubmit={saveProfile} className="rounded-3xl border border-stone-200 bg-white p-6 space-y-3">
                <h2 className="font-display text-2xl mb-1">Application profile</h2>
                <p className="text-xs text-stone-500 -mt-1 mb-2">The agent fills every job form from this. It never invents answers.</p>
                <div className="grid grid-cols-2 gap-3">
                  <Inp label="Full name *" value={p.full_name || ""} onChange={(e) => setP({ ...p, full_name: e.target.value })} data-testid="wa-name" />
                  <Inp label="Email *" value={p.email || ""} onChange={(e) => setP({ ...p, email: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Inp label="Phone *" value={p.phone || ""} onChange={(e) => setP({ ...p, phone: e.target.value })} />
                  <Inp label="City" value={p.city || ""} onChange={(e) => setP({ ...p, city: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Inp label="Degree" value={p.degree || ""} onChange={(e) => setP({ ...p, degree: e.target.value })} placeholder="B.Tech CSE" />
                  <Inp label="College" value={p.college || ""} onChange={(e) => setP({ ...p, college: e.target.value })} />
                  <Inp label="Grad year" type="number" value={p.grad_year || ""} onChange={(e) => setP({ ...p, grad_year: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Inp label="LinkedIn" value={p.linkedin_url || ""} onChange={(e) => setP({ ...p, linkedin_url: e.target.value })} />
                  <Inp label="GitHub" value={p.github_url || ""} onChange={(e) => setP({ ...p, github_url: e.target.value })} />
                  <Inp label="Portfolio" value={p.portfolio_url || ""} onChange={(e) => setP({ ...p, portfolio_url: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Inp label="Work authorization" value={p.work_authorization || ""} onChange={(e) => setP({ ...p, work_authorization: e.target.value })} placeholder="Indian citizen" />
                  <Inp label="Notice period" value={p.notice_period || ""} onChange={(e) => setP({ ...p, notice_period: e.target.value })} placeholder="Immediate" />
                  <Inp label="Expected CTC" value={p.expected_ctc || ""} onChange={(e) => setP({ ...p, expected_ctc: e.target.value })} placeholder="As per standards" />
                </div>
                <label className="block">
                  <span className="text-xs font-medium text-stone-500">About you (used in cover notes)</span>
                  <textarea rows={3} value={p.about || ""} onChange={(e) => setP({ ...p, about: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                </label>
                <div className="flex items-center gap-3">
                  <button type="submit" disabled={busy === "profile"} data-testid="wa-save-profile" className="btn-primary text-sm disabled:opacity-60">
                    {busy === "profile" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save profile
                  </button>
                  <label className="btn-ghost text-sm cursor-pointer">
                    {busy === "resume" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} {p.resume_filename || "Attach résumé PDF *"}
                    <input type="file" accept="application/pdf" onChange={uploadResume} className="hidden" data-testid="wa-resume" />
                  </label>
                </div>
              </form>

              <div className="rounded-3xl border border-stone-200 bg-white p-6 space-y-3">
                <h2 className="font-display text-2xl mb-1">Mandate — what the agent may do</h2>
                <Inp label="Target roles (comma separated)" value={rolesText} onChange={(e) => setRolesText(e.target.value)} placeholder="Data Analyst, Business Analyst" data-testid="wa-roles" />
                <Inp label="Preferred locations (empty = anywhere)" value={locText} onChange={(e) => setLocText(e.target.value)} placeholder="Bengaluru, Remote" />
                <Inp label="Never apply to (companies)" value={blockText} onChange={(e) => setBlockText(e.target.value)} placeholder="Company A, Company B" />
                <div>
                  <span className="text-xs font-medium text-stone-500">Autonomy</span>
                  <div className="mt-2 flex gap-2">
                    {[["copilot", "Ask me first (recommended)"], ["autopilot", "Full autopilot"]].map(([k, label]) => (
                      <button key={k} type="button" onClick={() => setPrefs({ ...prefs, autonomy: k })}
                        className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${prefs.autonomy === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-stone-400">Hard limits either way: max 5/day, {st.monthly_cap}/month, never the same company twice in 30 days.</p>
                <button onClick={savePrefs} disabled={busy === "prefs"} data-testid="wa-save-prefs" className="btn-primary text-sm disabled:opacity-60">
                  {busy === "prefs" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save mandate
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="wa-feed">
              <h2 className="font-display text-2xl mb-3">Agent activity</h2>
              {apps === null ? <p className="text-stone-500 text-sm">Loading…</p> : apps.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-500">
                  Nothing yet — complete your profile & mandate, then hit <b>Run now</b>. The agent also runs automatically every day.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {apps.map((a) => {
                    const m = STATUS_META[a.status] || STATUS_META.proposed;
                    const exec = a.artifacts?.execution;
                    return (
                      <div key={a.app_id} className="rounded-2xl border border-stone-200 bg-white px-5 py-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-stone-900">
                              {a.company} <span className="text-stone-400 font-normal">· {a.role || "Fresher role"}</span>
                              <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`}>{m.label}</span>
                              <span className="ml-1.5 text-[10px] font-mono uppercase text-stone-400">{a.ats}</span>
                            </p>
                            {a.artifacts?.cover_note && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2 max-w-2xl italic">"{a.artifacts.cover_note}"</p>}
                            {exec?.note && <p className="text-[11px] text-stone-400 mt-0.5">{exec.note}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <a href={a.apply_url} target="_blank" rel="noreferrer" className="text-xs text-stone-400 hover:text-stone-900 inline-flex items-center gap-1">Listing <ExternalLink size={11} /></a>
                            {a.status === "proposed" && (
                              <>
                                <button onClick={() => act(() => wishagentDismiss(a.app_id), a.app_id)} disabled={busy === a.app_id}
                                  className="text-xs px-3 py-1.5 rounded-full border border-stone-300 text-stone-500 hover:border-stone-900 disabled:opacity-60">Skip</button>
                                <button onClick={() => act(() => wishagentApprove(a.app_id), a.app_id)} disabled={busy === a.app_id} data-testid={`wa-approve-${a.app_id}`}
                                  className="text-xs font-medium px-4 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-60 inline-flex items-center gap-1.5">
                                  {busy === a.app_id ? <Loader2 size={12} className="animate-spin" /> : <><Send size={11} /> Apply</>}
                                </button>
                              </>
                            )}
                            {a.status === "submitted" && <CheckCircle2 size={16} className="text-emerald-600" />}
                            {a.status === "failed" && <XCircle size={16} className="text-red-500" />}
                            {a.status === "prepared" && (
                              <a href={a.apply_url} target="_blank" rel="noreferrer"
                                className="text-xs font-medium px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1">
                                Finish <ExternalLink size={11} />
                              </a>
                            )}
                          </div>
                        </div>
                        {exec?.prepared_answers?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-stone-100 space-y-1.5">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Drafted answers — copy into the form</p>
                            {exec.prepared_answers.slice(0, 6).map((qa, i) => (
                              <p key={i} className="text-xs text-stone-600"><b>{qa.q}</b> — {qa.a || <span className="text-orange-600">needs you</span>}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
